<!--
// app/components/pages/teams-team/GridSquadPlayers.vue
-->
<script setup lang="ts">
import type { Player, PlayerRole } from "~/src/player/domain/Player";
import type { Team } from "~/src/team/domain/models/Team";
import type { SeasonStatsPartner } from "~/src/season-cluster/domain/models/01.Season";
import { $LEGEND_PLAYER_IDS } from "~/src/player/domain/services/playerService";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import CardDraftPlayer from "~/app/components/Shared/players/CardDraftPlayer.vue";
import CardPlayerFeaturedCategory from "~/app/components/Shared/players/CardPlayerFeaturedCategory.vue";

type Props = {
  players: Player[];
  seasonId: number;
  regionId: number;
  team?: Team;
  seasonMvpPartner?: SeasonStatsPartner;
};

const props = defineProps<Props>();

const { currentSeason, region } = useCompetitionStore();

const allDraftPlayers = props.players.filter(player => player.category === "draft" || !player.category);
const allWildcardPlayers = props.players.filter(player => player.category === "wildcard");

//filtered by position players

const draftPlayers = {
  goalkeepers: filterPlayersByPosition(allDraftPlayers, "goalkeeper"),
  defenders: filterPlayersByPosition(allDraftPlayers, "defender"),
  midfielders: filterPlayersByPosition(allDraftPlayers, "midfielder"),
  forwards: filterPlayersByPosition(allDraftPlayers, "forward"),
};

const wildcardPlayers = {
  goalkeepers: filterPlayersByPosition(allWildcardPlayers, "goalkeeper"),
  defenders: filterPlayersByPosition(allWildcardPlayers, "defender"),
  midfielders: filterPlayersByPosition(allWildcardPlayers, "midfielder"),
  forwards: filterPlayersByPosition(allWildcardPlayers, "forward"),
};

//util
const checkIfIsPLayerLegend = (playerId: number): boolean => {
  const legendsIds = $LEGEND_PLAYER_IDS || [];
  return legendsIds.includes(playerId);
};

function filterPlayersByPosition(players: Player[], position: PlayerRole) {
  return players
    .filter(player => player.role === position)
    .sort((a, b) => a.shortName?.localeCompare(b.shortName));
}
</script>

<template>
  <!--draft-->
  <!--goalkeepers-->
  <card-draft-player
    v-for="player in draftPlayers.goalkeepers"
    :key="player.id"
    :team-first-color-h-e-x="team?.firstColorHEX"
    :team-second-color-h-e-x="team?.secondColorHEX"
    :player-complete-name="player.shortName"
    :player-number="player.jersey"
    :player-position="player.role"
    :player-image="player.image"
    :season-id="seasonId"
    :player-id="player.id"
    :player="player"
    :mvp-partner="seasonMvpPartner"
    :player-team="team"
    :show-clause="regionId !== 5"
  ></card-draft-player>

  <!--defenders-->
  <card-draft-player
    v-for="player in draftPlayers.defenders"
    :key="player.id"
    :team-first-color-h-e-x="team?.firstColorHEX"
    :team-second-color-h-e-x="team?.secondColorHEX"
    :player-complete-name="player.shortName"
    :player-number="player.jersey"
    :player-position="player.role"
    :player-image="player.image"
    :season-id="seasonId"
    :player-id="player.id"
    :player="player"
    :mvp-partner="seasonMvpPartner"
    :player-team="team"
    :show-clause="regionId !== 5"
  ></card-draft-player>

  <!--midfielders-->
  <card-draft-player
    v-for="player in draftPlayers.midfielders"
    :key="player.id"
    :team-first-color-h-e-x="team?.firstColorHEX"
    :team-second-color-h-e-x="team?.secondColorHEX"
    :player-complete-name="player.shortName"
    :player-number="player.jersey"
    :player-position="player.role"
    :player-image="player.image"
    :season-id="seasonId"
    :player-id="player.id"
    :player="player"
    :mvp-partner="seasonMvpPartner"
    :player-team="team"
    :show-clause="regionId !== 5"
  ></card-draft-player>

  <!--forwards-->
  <card-draft-player
    v-for="player in draftPlayers.forwards"
    :key="player.id"
    :team-first-color-h-e-x="team?.firstColorHEX"
    :team-second-color-h-e-x="team?.secondColorHEX"
    :player-complete-name="player.shortName"
    :player-number="player.jersey"
    :player-position="player.role"
    :player-image="player.image"
    :season-id="seasonId"
    :player-id="player.id"
    :player="player"
    :mvp-partner="seasonMvpPartner"
    :player-team="team"
    :show-clause="regionId !== 5"
  ></card-draft-player>

  <!--wildcards-->
  <!--goalkeepers-->
  <card-player-featured-category
    v-for="player in wildcardPlayers.goalkeepers"
    :key="player.id"
    :no-link="checkIfIsPLayerLegend(player.id)"
    :player="player"
    :player-complete-name="player.shortName"
    :player-number="player.jersey"
    :player-category="player.category"
    :player-position="player.role"
    :player-image="player.image"
    :player-team="team"
    :season-id="seasonId"
    :player-id="player.id"
    :is-injured="player.metaInformation?.injury === 'true' && currentSeason.id === seasonId"
    :mvp-partner="seasonMvpPartner"
  ></card-player-featured-category>

  <!--defenders-->
  <card-player-featured-category
    v-for="player in wildcardPlayers.defenders"
    :key="player.id"
    :no-link="checkIfIsPLayerLegend(player.id)"
    :player="player"
    :player-complete-name="player.shortName"
    :player-number="player.jersey"
    :player-category="player.category"
    :player-position="player.role"
    :player-image="player.image"
    :player-team="team"
    :season-id="seasonId"
    :player-id="player.id"
    :is-injured="player.metaInformation?.injury === 'true' && currentSeason.id === seasonId"
    :mvp-partner="seasonMvpPartner"
  ></card-player-featured-category>

  <!--midfielders-->
  <card-player-featured-category
    v-for="player in wildcardPlayers.midfielders"
    :key="player.id"
    :no-link="checkIfIsPLayerLegend(player.id)"
    :player="player"
    :player-complete-name="player.shortName"
    :player-number="player.jersey"
    :player-category="player.category"
    :player-position="player.role"
    :player-image="player.image"
    :player-team="team"
    :season-id="seasonId"
    :player-id="player.id"
    :is-injured="player.metaInformation?.injury === 'true' && currentSeason.id === seasonId"
    :mvp-partner="seasonMvpPartner"
  ></card-player-featured-category>

  <!--forwards-->
  <card-player-featured-category
    v-for="player in wildcardPlayers.forwards"
    :key="player.id"
    :no-link="checkIfIsPLayerLegend(player.id)"
    :player="player"
    :player-complete-name="player.shortName"
    :player-number="player.jersey"
    :player-category="player.category"
    :player-position="player.role"
    :player-image="player.image"
    :player-team="team"
    :season-id="seasonId"
    :player-id="player.id"
    :is-injured="player.metaInformation?.injury === 'true' && currentSeason.id === seasonId"
    :mvp-partner="seasonMvpPartner"
  ></card-player-featured-category>
</template>

<style scoped></style>
